import Cookies from "js-cookie";
import { baseApi } from "./baseApi";
import { setAuthUser } from "../slices/authSlice";

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // signup
    signup: builder.mutation<
      any,
      {
        name: string;
        email: string;
        password: string;
        password_confirmation: string;
        account_type_id: string;
        terms_conditions: boolean; // ✅ added
      }
    >({
      query: (body) => ({
        url: "/register",
        method: "POST",
        body,
      }),
    }),

    // verify signup token
    verifySignupOTP: builder.mutation<any, { email: string; token: string }>({
      query: (body) => ({
        url: "/verify-token",
        method: "POST",
        body,
      }),
    }),

    // resend token for signup
    resendVerificationEmail: builder.mutation({
      query: (body: { email: string }) => ({
        url: "/resend-verification-token",
        method: "POST",
        body,
      }),
    }),

    // otp for forgot password
    forgotPassword: builder.mutation({
      query: (body: { email: string }) => ({
        url: "/forgot-password",
        method: "POST",
        body,
      }),
    }),

    // resend otp for forgot password
    resendResetOtp: builder.mutation({
      query: (body: { email: string }) => ({
        url: "/resend-otp",
        method: "POST",
        body,
      }),
    }),

    // reset password
    resetPassword: builder.mutation({
      query: (body: {
        password: string;
        email: string;
        token: string;
        password_confirmation: string;
      }) => ({
        url: "/reset-password",
        method: "POST",
        body,
      }),
    }),

    // login
    login: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
      // Keep Retry-After (seconds) with the error so a locked account can show
      // a countdown. It's null unless the API exposes the header via CORS.
      transformErrorResponse: (response, meta) => ({
        ...response,
        retryAfter: (meta as { response?: Response } | undefined)?.response?.headers.get("Retry-After") ?? null,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const token = data?.data?.token;
          const admin = data?.data?.admin;

          Cookies.set("token", token, {
            expires: 7,
            secure: false,
            sameSite: "Lax",
          });
          localStorage.setItem("token", token);
          localStorage.setItem("loginTimestamp", String(Date.now()));

          // Include the user: without it setAuthUser would clear the stored user.
          dispatch(setAuthUser({ token, admin, user: data?.data?.user }));
        } catch (error: any) {
          console.error("Login error:", error);
        }
      },
    }),

    // logout
    logout: builder.mutation<any, void>({
      query: () => {
        const token = Cookies.get("token");
        return {
          url: "/logout",
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
    }),

    // change expired password
    changeExpiredPassword: builder.mutation({
      query: (body: {
        email: string;
        password: string;
        password_confirmation: string;
      }) => ({
        url: "/PasswordExpiry",
        method: "POST",
        body,
      }),
    }),

    enable2Fa: builder.mutation({
      query: (password: string) => ({
        url: "/settings/TwoFa/2fa/enable",
        method: "POST",
        body: { password },
      }),
      invalidatesTags: ["users"],
    }),

    disable2Fa: builder.mutation({
      query: (password: string) => ({
        url: "/settings/TwoFa/2fa/disable",
        method: "POST",
        body: { password },
      }),
      invalidatesTags: ["users"],
    }),

    verifyOTP: builder.mutation({
      query: (otp: string) => ({
        url: "/settings/TwoFa/2fa/verify",
        method: "POST",
        body: { otp },
      }),
      invalidatesTags: ["users"],
    }),

    verifyLoginOTP: builder.mutation({
      query: (otp: string) => ({
        url: "/otp/verify",
        method: "POST",
        body: { otp },
      }),
      invalidatesTags: ["users"],
    }),

    updateUserProfile: builder.mutation({
      query: ({
        id,
        formData,
        ...rest
      }: {
        id: string | number;
        formData?: FormData;
        [key: string]: any;
      }) => ({
        url: `/settings/profile/${id}`,
        method: "POST",
        body: formData || rest,
      }),
      invalidatesTags: ["users"],
    }),

    updateProfileName: builder.mutation<any, { name: string }>({
      query: ({ name }) => ({
        url: "/profile/name",
        method: "PATCH",
        body: { name },
      }),
      invalidatesTags: ["users"],
    }),

    updateNotifyPref: builder.mutation({
      query: ({
        id,
        notify_email,
        notify_inapp,
      }: {
        id: string | number;
        notify_email: boolean;
        notify_inapp: boolean;
      }) => ({
        url: `settings/notification/admins/${id}/preferences`,
        method: "PUT",
        body: { notify_email, notify_inapp },
      }),
      invalidatesTags: ["users"],
    }),

    // change password
    changePassword: builder.mutation({
      query: (body: {
        old_password: string;
        new_password: string;
        new_password_confirmation: string;
      }) => ({
        url: "/settings/changePassword",
        method: "POST",
        body,
      }),
    }),

    // get current user
    getCurrentUser: builder.query<any, void>({
      query: () => ({
        url: "/me",
        method: "GET",
      }),
      providesTags: ["users"],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          const user = data?.data;
          if (user) {
            dispatch(setAuthUser({ token: Cookies.get("token") || "", user }));
          }
        } catch (error) {
          console.error("Get current user error:", error);
        }
      },
    }),

    // get account types
    getAccountTypes: builder.query<
      { id: string; name: string }[],
      void
    >({
      query: () => ({
        url: "/account-types",
        method: "GET",
      }),
      transformResponse: (response: any) => response?.data ?? [],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useChangePasswordMutation,
  useEnable2FaMutation,
  useDisable2FaMutation,
  useVerifyOTPMutation,
  useUpdateUserProfileMutation,
  useUpdateProfileNameMutation,
  useUpdateNotifyPrefMutation,
  useVerifyLoginOTPMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangeExpiredPasswordMutation,
  useSignupMutation,
  useVerifySignupOTPMutation,
  useResendVerificationEmailMutation,
  useResendResetOtpMutation,
  useGetCurrentUserQuery,
  useGetAccountTypesQuery,
} = authApi;